/**
 * Éditeur d'image avec repères : choisir un schéma ou une photo de la médiathèque, puis toucher l'image pour poser un
 * repère numéroté (nom et explication). Dans la vidéo, la caméra s'approche de chaque repère quand la voix le nomme.
 */
import { useRef } from "react";
import { Link } from "react-router";
import { Trash2 } from "lucide-react";
import type { Asset, Block } from "../lib/api";
import { Field } from "./ui";

type ImageBlockT = Extract<Block, { type: "image" }>;
const slug = (s: string) => s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 40);

export function ImageEditor({ block, lang, images, onChange }: { block: ImageBlockT; lang: string; images: Asset[]; onChange: (b: ImageBlockT) => void }) {
  const img = images.find((a) => a.id === block.assetId) ?? null;
  const box = useRef<HTMLDivElement>(null);
  const set = (patch: Partial<ImageBlockT>) => onChange({ ...block, ...patch });
  const setMarker = (i: number, patch: Partial<ImageBlockT["markers"][number]>) => set({ markers: block.markers.map((m, k) => (k === i ? { ...m, ...patch } : m)) });
  const add = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!box.current || block.markers.length >= 12) return;
    const r = box.current.getBoundingClientRect();
    const x = Math.round(((e.clientX - r.left) / r.width) * 1000) / 1000, y = Math.round(((e.clientY - r.top) / r.height) * 1000) / 1000;
    const n = block.markers.length + 1;
    let id = `repere-${n}`;
    while (block.markers.some((m) => m.id === id)) id = `${id}-b`;
    set({ markers: [...block.markers, { id, x, y, label: { fr: `Repère ${n}` }, text: {} }] });
  };

  return (
    <div className="stack" style={{ gap: 10 }}>
      <div className="row wrap" style={{ gap: 8, alignItems: "flex-end" }}>
        <div style={{ flex: "2 1 260px" }}>
          <Field label="Image de la médiathèque">
            <select className="select" value={block.assetId} onChange={(e) => set({ assetId: e.target.value })}>
              <option value="">— Choisir une image —</option>
              {block.assetId && !img && <option value={block.assetId}>{block.assetId} (non disponible)</option>}
              {images.map((a) => <option key={a.id} value={a.id}>{a.title}</option>)}
            </select>
          </Field>
        </div>
        <Link to="/medias" target="_blank" rel="noopener" className="btn sm ghost" style={{ height: 36 }}>Ajouter une image ↗</Link>
      </div>
      <Field label={`Description de l'image (lue sans connexion et par les lecteurs d'écran)`}>
        <input className="input" maxLength={200} value={block.alt[lang] ?? ""} onChange={(e) => set({ alt: { ...block.alt, [lang]: e.target.value } })} placeholder="Ex. Coupe du cœur humain : oreillettes, ventricules, valves." />
      </Field>
      {img?.url ? (
        <>
          <span className="small muted">Touchez l'image pour poser un repère ({block.markers.length}/12).</span>
          <div ref={box} onClick={add} style={{ position: "relative", width: "100%", maxWidth: 640, aspectRatio: String(block.aspect), borderRadius: 12, overflow: "hidden", background: "var(--surface-2, #F6F7F9)", cursor: "crosshair" }}>
            <img src={img.url} alt="" draggable={false} style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "contain" }}
              onLoad={(e) => { const t = e.currentTarget; if (t.naturalWidth && t.naturalHeight) { const a = Math.round((t.naturalWidth / t.naturalHeight) * 1000) / 1000; if (Math.abs(a - block.aspect) > 0.01) set({ aspect: Math.max(0.2, Math.min(5, a)) }); } }} />
            {block.markers.map((m, i) => (
              <span key={m.id} style={{ position: "absolute", left: `${m.x * 100}%`, top: `${m.y * 100}%`, transform: "translate(-50%, -50%)", width: 28, height: 28, borderRadius: 999, border: "2px solid #fff", background: "var(--accent, #0E6B66)", color: "#fff", fontWeight: 800, fontSize: 12, display: "grid", placeItems: "center", boxShadow: "0 1px 4px rgba(0,0,0,0.3)", pointerEvents: "none" }}>{i + 1}</span>
            ))}
          </div>
        </>
      ) : <span className="small muted">Choisissez une image vérifiée pour poser les repères.</span>}
      {block.markers.map((m, i) => (
        <div key={m.id} className="row wrap" style={{ gap: 8, alignItems: "center" }}>
          <span className="chip static">{i + 1}</span>
          <input className="input" style={{ flex: "1 1 160px" }} maxLength={60} value={m.label[lang] ?? ""} placeholder={m.label.fr ? `FR : ${m.label.fr}` : "Nom de la zone"}
            onChange={(e) => { const label = { ...m.label, [lang]: e.target.value }; setMarker(i, lang === "fr" && slug(e.target.value) && !block.markers.some((x, k) => k !== i && x.id === slug(e.target.value)) ? { label, id: slug(e.target.value) } : { label }); }} aria-label={`Nom du repère ${i + 1}`} />
          <input className="input" style={{ flex: "2 1 240px" }} maxLength={300} value={m.text[lang] ?? ""} placeholder="Explication (une phrase)" onChange={(e) => setMarker(i, { text: { ...m.text, [lang]: e.target.value } })} aria-label={`Explication du repère ${i + 1}`} />
          <button type="button" className="icon-btn bordered" aria-label={`Supprimer le repère ${i + 1}`} onClick={() => set({ markers: block.markers.filter((_, k) => k !== i) })}><Trash2 size={15} /></button>
        </div>
      ))}
    </div>
  );
}
